/**
 * NOTE: This file is part of the legacy Notes feature.
 * User-facing text has been renamed to "Documentation" for clarity.
 * For the new Notes feature, check the Hub implementation.
 */

import { Note } from "@/lib/api/notes.ts";
import { Call, useGetCallLogs } from "@/lib/api/calls.ts";
import { Fragment, ReactNode, useMemo } from "react";
import { Loader2 } from "lucide-react";

interface NotesWithCallLogsProps {
  mentorshipId?: string;

  notes: Note[];

  renderNote: (note: Note) => ReactNode;
  renderCallLog: (callLog: Call) => ReactNode;
}

interface NoteWithCallLog extends Note {
  type: "note";
}

interface CallLogWithNote extends Call {
  type: "call";
}

type NoteOrCallLog = NoteWithCallLog | CallLogWithNote;

export function NotesWithCallLogs({
  mentorshipId,
  notes,
  renderCallLog,
  renderNote,
}: NotesWithCallLogsProps) {
  const { data: callLogs, isLoading: isCallLogsLoading } =
    useGetCallLogs(mentorshipId);

  const list = useMemo(() => {
    const listArr: NoteOrCallLog[] = [];

    notes.forEach((note) => {
      listArr.push({
        ...note,
        type: "note",
      });
    });

    callLogs?.forEach((callLog) => {
      listArr.push({
        ...callLog,
        type: "call",
      });
    });

    return listArr.toSorted((a, b) => {
      const aDate = a.type === "note" ? a.createdAt : a.created_at;
      const bDate = b.type === "note" ? b.createdAt : b.created_at;

      return Date.parse(bDate) - Date.parse(aDate);
    });
  }, [callLogs, notes]);

  return (
    <>
      {isCallLogsLoading ? (
        <div className="mt-4 flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        list.map((item) => {
          if (item.type === "note") {
            return <Fragment key={item.id}>{renderNote(item)}</Fragment>;
          } else {
            return <Fragment key={item.id}>{renderCallLog(item)}</Fragment>;
          }
        })
      )}
    </>
  );
}
